import React, { useCallback } from "react";

// Components
import Modal from "../modal";

// Steps
import FlashcardSetupStep from "./FlashcardSetupStep";
import FlashcardUploadStep from "./FlashcardUploadStep";

// SVGs
import Star1 from "../Svgs/Star1";
import Star2 from "../Svgs/Star2";

// Icons
import { ReactComponent as CloseIcon } from 'images/close-stroke.svg';
import { ReactComponent as LeftArrowIcon } from 'images/left-arrow-stroke.svg';

// Context
import { FlashcardSetupProvider, useFlashcardSetup } from "./FlashcardSetupContext";
import { useTranslation } from "react-i18next";

// Styles
import styles from "./styles.module.scss";
import { APIId } from "../../types";

// Utils
import useIsDarkMode from "../../utils/hooks/darkMode";
import Typography from "../Typography";
import HelpTooltip from "../HelpTooltip";

// Assets

type FlashcardSetupModalInnerProps = {
  className?: string,
  studyTools?: boolean,
  dashboard?: boolean,
  drawer?: boolean,
  mobile_dashboard?: boolean,
  open?: boolean,
  onClose?: () => void
}

function FlashcardSetupModalInner(props: FlashcardSetupModalInnerProps) {
  const { state, intializedWithSmartNotes, dispatch } = useFlashcardSetup();
  const { t } = useTranslation();

  const steps = [
    <FlashcardSetupStep key="flashcardSetupStep" />,
    <FlashcardUploadStep key="flashcardUploadStep" />
  ]

  const handleTabClick = useCallback((stepNumber: number) => {
    if (stepNumber === 1) {
      dispatch({ type: "PREV_STEP" });
    }
  }, [dispatch]);

  const handleBackClick = useCallback(() => {
    handleTabClick(1);
  }, [handleTabClick]);

  const customTitle = (
    <div className={styles.header}>
      <div className="w-8 h-8">
        {state.step !== 1 && !intializedWithSmartNotes && (
          <button
            className="flex justify-center items-center rounded-full bg-stone-50 hover:opacity-80 dark:hover:bg-zeus-900 dark:bg-zeus-800 h-8 w-8 relative z-10"
            onClick={handleBackClick}
          >
            <LeftArrowIcon className="w-[10px] h-[10px] stroke-3 dark:text-white" />
          </button>
        )}
      </div>
      
      <div className='flex items-center gap-2 dark:text-white'>
        {t('flashcardSetupModal.title')}
        <HelpTooltip type="flashcard" />
      </div>

      <button
        className="flex justify-center items-center rounded-full bg-stone-50 hover:opacity-80 dark:hover:bg-zeus-900 dark:bg-zeus-800 h-8 w-8 relative z-10"
        onClick={props.onClose || (() => { })}
      >
        <CloseIcon className="w-[10px] h-[10px] fill-stone-700 dark:text-white stroke-3" />
      </button>
    </div>
  );

  const tabLines = !intializedWithSmartNotes && (
    <div className={styles.tab_lines}>
      <div 
        className={`${styles.tab_line} ${state.step >= 1 ? styles.active : ''}`}
        onClick={() => handleTabClick(1)}
      />
      <div 
        className={`${styles.tab_line} ${state.step >= 2 ? styles.active : ''}`}
        onClick={() => state.step >= 2 && handleTabClick(2)}
      />
    </div>
  );

  return (
    <>
      {props.open && (
        <Modal
          className={styles.modal}
          showCloseButton={false}
          classes={{ content: "w-[580px]" }}
          open={props.open}
          onClose={props.onClose || (() => { })}
          title={customTitle}
          tabLines={tabLines}
        >
          {steps[state.step - 1]}
        </Modal>
      )}
    </>
  );
}

type FlashcardSetupModalProps = {
  smartNoteIdscope?: APIId[],
  className?: string,
  studyTools?: boolean,
  dashboard?: boolean,
  drawer?: boolean,
  mobile_dashboard?: boolean,
  open: boolean,
  onClose: () => void,
  languages?: Record<string, string>
}

export default function FlashcardSetupModal(props: FlashcardSetupModalProps & Record<string, any>) {
  return (
    <FlashcardSetupProvider {...props}>
      <FlashcardSetupModalInner className={props.className} studyTools={props.studyTools} dashboard={props.dashboard} drawer={props.drawer} mobile_dashboard={props.mobile_dashboard} open={props.open} onClose={props.onClose} />
    </FlashcardSetupProvider>
  );
}

